import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { cloneDeep } from 'lodash';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import {
  SyberryTodaySpentTimeActivity,
  type SyberryTodayConfirmCheckInPayload,
  type SyberryTodayPlanning,
  type SyberryTodayService,
  type SyberryTodaySpentTimePayload,
  type SyberryTodayTaskId,
  type SyberryTodayWorkItem,
} from './types';
import { useSyberryTodayAllocationApiClient, type SyberryTodayAllocationApiClient } from './api/allocationClient';
import { mapSyberryTodayAllocationResponseToSummaries } from './api/allocationMapper';
import { ST_SPENT_TIME_ACTIVITY_ID } from './api/config';
import {
  SyberryTodayApiPersonalBacklogCategory,
  type SyberryTodayApiOrderPosition,
  type SyberryTodayApiResponse,
  type SyberryTodayApiUpdateIssueOrderRequest,
} from './api/types';
import { useSyberryTodayApiClient, type SyberryTodayApiClient } from './api/client';
import { mapSyberryTodayApiResponseToPlanning } from './api/mapper';
import { getUniqueExistingTaskIds } from './syberryTodayTaskSelectionUtils';

const INDIVIDUAL_TASKS_PROJECT_SLUG = 'individual-tasks';

const TIME_ENTRY_ACTIVITY_ID_BY_ACTIVITY: Record<SyberryTodaySpentTimeActivity, number> = {
  [SyberryTodaySpentTimeActivity.OFFICE]: ST_SPENT_TIME_ACTIVITY_ID.OFFICE,
  [SyberryTodaySpentTimeActivity.HOME]: ST_SPENT_TIME_ACTIVITY_ID.HOME,
  [SyberryTodaySpentTimeActivity.TRANSIT]: ST_SPENT_TIME_ACTIVITY_ID.TRANSIT,
};

export type SyberryTodayPlanningApi = Pick<
  SyberryTodayService,
  | 'getTodayPlanning'
  | 'saveTaskSelection'
  | 'markTaskAsOpened'
  | 'confirmCheckIn'
  | 'postponePlanning'
  | 'skipCheckInCompletely'
  | 'reorderQueueIssues'
>;

export type SyberryTodayAllocationApi = Pick<SyberryTodayService, 'getTodayAllocations'>;

const loadPlanning = async (
  client: SyberryTodayApiClient,
  response?: SyberryTodayApiResponse,
): Promise<SyberryTodayPlanning> => mapSyberryTodayApiResponseToPlanning(response ?? (await client.getSyberryToday()));

const buildOrderPositions = (
  orderedTaskIds: SyberryTodayTaskId[],
  oldPositionByTaskId: Map<SyberryTodayTaskId, number | undefined>,
): SyberryTodayApiUpdateIssueOrderRequest['positions'] =>
  Object.fromEntries(
    orderedTaskIds.map((taskId, index): [string, SyberryTodayApiOrderPosition] => {
      const oldPosition = oldPositionByTaskId.get(taskId);

      return [taskId, oldPosition != null ? { position: index + 1, oldPosition } : { position: index + 1 }];
    }),
  );

const hasOrderPositions = (request: SyberryTodayApiUpdateIssueOrderRequest) =>
  Object.keys(request.positions).length > 0;

const getProjectSlug = (projectName: string) =>
  projectName
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '-');

const isPersonalBacklogOrderableTask = (task: SyberryTodayWorkItem) =>
  getProjectSlug(task.projectName) !== INDIVIDUAL_TASKS_PROJECT_SLUG;

const buildBacklogOrderRequest = (
  orderedTaskIds: SyberryTodayTaskId[],
  planning: SyberryTodayPlanning,
): SyberryTodayApiUpdateIssueOrderRequest => {
  const orderableTaskIds = new Set(
    planning.tasks.filter(task => isPersonalBacklogOrderableTask(task)).map(task => task.id),
  );

  return {
    positions: buildOrderPositions(
      orderedTaskIds.filter(taskId => orderableTaskIds.has(taskId)),
      new Map(planning.tasks.map(task => [task.id, task.personalBacklogPosition])),
    ),
  };
};

const buildQueueOrderRequest = (
  orderedTaskIds: SyberryTodayTaskId[],
  planning: SyberryTodayPlanning,
): SyberryTodayApiUpdateIssueOrderRequest => ({
  positions: buildOrderPositions(
    orderedTaskIds,
    new Map(planning.tasks.map(task => [task.id, task.customQueuePosition])),
  ),
});

const runPlanningMutation = async (
  client: SyberryTodayApiClient,
  runMutations: () => Promise<unknown>,
): Promise<SyberryTodayPlanning> => {
  await runMutations();

  return loadPlanning(client);
};

export const createSyberryTodayPlanningApi = (client: SyberryTodayApiClient): SyberryTodayPlanningApi => {
  const getTodayPlanning = () => loadPlanning(client);

  const saveTaskSelection = (taskIds: SyberryTodayTaskId[], planningSnapshot?: SyberryTodayPlanning) =>
    runPlanningMutation(client, async () => {
      const planning = planningSnapshot ?? (await loadPlanning(client));
      const validIds = new Set(planning.tasks.map(task => task.id));
      const selectedIds = getUniqueExistingTaskIds(validIds, taskIds);
      const selectedIdSet = new Set(selectedIds);
      const currentTodayIdSet = new Set(planning.selectedTaskIds);

      const removedIds = planning.selectedTaskIds.filter(taskId => !selectedIdSet.has(taskId));
      const addedIds = selectedIds.filter(taskId => !currentTodayIdSet.has(taskId));

      for (const taskId of removedIds) {
        await client.updateIssueCategory(taskId, {
          personalBacklogCategory: SyberryTodayApiPersonalBacklogCategory.ALL,
        });
      }

      for (const taskId of addedIds) {
        await client.updateIssueCategory(taskId, {
          personalBacklogCategory: SyberryTodayApiPersonalBacklogCategory.TODAY,
        });
      }

      const orderRequest = buildBacklogOrderRequest(selectedIds, planning);

      if (hasOrderPositions(orderRequest)) {
        await client.updatePersonalBacklogOrder(orderRequest);
      }
    });

  const markTaskAsOpened = async (taskId: SyberryTodayTaskId) => {
    await client.updateIssueCategory(taskId, {
      personalBacklogCategory: SyberryTodayApiPersonalBacklogCategory.ALL,
    });
  };

  const confirmCheckIn = ({ taskIds }: SyberryTodayConfirmCheckInPayload) =>
    runPlanningMutation(client, async () => {
      const planning = await loadPlanning(client);
      const currentTodayIdSet = new Set(planning.selectedTaskIds);
      const providedOrderedIds = getUniqueExistingTaskIds(currentTodayIdSet, taskIds);
      const providedIdSet = new Set(providedOrderedIds);
      const missingTodayIds = planning.selectedTaskIds.filter(taskId => !providedIdSet.has(taskId));
      const orderedIds = [...providedOrderedIds, ...missingTodayIds];

      const orderRequest = buildBacklogOrderRequest(orderedIds, planning);

      if (hasOrderPositions(orderRequest)) {
        await client.updatePersonalBacklogOrder(orderRequest);
      }

      await client.confirmCheckin({ dailyCheckin: { reviewedInbox: true, plannedToday: true } });
    });

  const postponePlanning = () => runPlanningMutation(client, () => client.postponeCheckin());

  const skipCheckInCompletely = () => runPlanningMutation(client, () => client.skipCheckin());

  const reorderQueueIssues = (queueId: number, orderedTaskIds: SyberryTodayTaskId[]) =>
    runPlanningMutation(client, async () => {
      const planning = await loadPlanning(client);
      const queueTaskIds = new Set(
        planning.tasks.filter(task => task.streamId === String(queueId)).map(task => task.id),
      );
      const orderedIds = getUniqueExistingTaskIds(queueTaskIds, orderedTaskIds);

      if (orderedIds.length) {
        await client.updateQueueIssueOrder(queueId, buildQueueOrderRequest(orderedIds, planning));
      }
    });

  return {
    getTodayPlanning,
    saveTaskSelection,
    markTaskAsOpened,
    confirmCheckIn,
    postponePlanning,
    skipCheckInCompletely,
    reorderQueueIssues,
  };
};

export const createSyberryTodayAllocationApi = ({
  allocationClient,
  getEmployeeAlias,
}: {
  allocationClient: SyberryTodayAllocationApiClient;
  getEmployeeAlias: () => Nullable<string>;
}): SyberryTodayAllocationApi => {
  const getTodayAllocations = async () => {
    const employeeAlias = getEmployeeAlias();

    if (!employeeAlias) return [];

    const response = await allocationClient.getCurrentWeekAllocation(employeeAlias);

    return mapSyberryTodayAllocationResponseToSummaries(response);
  };

  return {
    getTodayAllocations,
  };
};

export const createSyberryTodaySpentTimeApi = (
  client: SyberryTodayApiClient,
  activityIdByActivity: Record<SyberryTodaySpentTimeActivity, number> = TIME_ENTRY_ACTIVITY_ID_BY_ACTIVITY,
): Pick<SyberryTodayService, 'createSpentTime'> => {
  const createSpentTime = async (payload: SyberryTodaySpentTimePayload) => {
    await client.createIssueTimeEntry(payload.taskId, {
      timeSpent: payload.timeSpent,
      activityId: activityIdByActivity[payload.activity],
      ...(payload.date ? { spentOn: payload.date } : {}),
      ...(payload.comment ? { comments: payload.comment } : {}),
    });

    return cloneDeep(payload);
  };

  return {
    createSpentTime,
  };
};

const useSyberryTodayService = (): SyberryTodayService => {
  const client = useSyberryTodayApiClient();
  const allocationClient = useSyberryTodayAllocationApiClient();
  const { getUserAlias } = useAuthenticationProvider();
  const getUserAliasRef = useRef(getUserAlias);

  useLayoutEffect(() => {
    getUserAliasRef.current = getUserAlias;
  }, [getUserAlias]);

  const getEmployeeAlias = useCallback(() => getUserAliasRef.current(), []);

  return useMemo<SyberryTodayService>(() => {
    const planningApi = createSyberryTodayPlanningApi(client);
    const allocationApi = createSyberryTodayAllocationApi({
      allocationClient,
      getEmployeeAlias,
    });
    const { createSpentTime } = createSyberryTodaySpentTimeApi(client);

    return {
      ...planningApi,
      ...allocationApi,
      createSpentTime,
    };
  }, [allocationClient, client, getEmployeeAlias]);
};

export default useSyberryTodayService;
